/**
 * operatorProvisioningFaq — curated, rule-based setup guidance for the Operator
 * Setup Assistant. GUIDANCE ONLY: static content, no LLM, no network, no
 * secrets. Later this can be swapped for a live LLM once a provider + key
 * handling + cost controls are approved.
 */

// Per-wizard-step starter questions (keys match STEP ids in the wizard).
export const STEP_STARTERS = {
  company: ["corp_vs_operator", "need_own_domain"],
  licence: ["operating_area", "corp_vs_operator"],
  owner_admin: ["first_admin", "first_login_test"],
  dispatcher: ["dispatcher_number", "first_admin"],
  twilio: ["twilio_sid", "twilio_auth_token", "public_booking_number", "twilio_owner", "existing_number"],
  email: ["resend_what", "verify_domain", "dkim", "spf", "mx", "emails_failing"],
  branding: ["need_own_domain", "support_email"],
  fares: ["fares_help"],
  payments: ["stripe_why", "stripe_keys", "stripe_test"],
  review: ["go_live_tests", "db_hosting", "corp_vs_operator"],
};

// FAQ entries: id -> { q, a }. Plain-text answers (kept factual + provider-neutral).
export const FAQ = {
  corp_vs_operator: {
    q: "What's the difference between a corporate customer and a taxi operator?",
    a: "A CORPORATE CUSTOMER just uses an existing Dispatch OS deployment: they register their company, get approved, add staff, book rides and receive statements. They do NOT need Twilio, Resend, Stripe, DNS or hosting.\n\nA TAXI OPERATOR wants their OWN separate Dispatch OS deployment. They DO need their own Twilio (numbers + SMS/voice), Resend (email), optionally Stripe (card payments), a domain/DNS, an admin account and a deployment pack. This wizard is for taxi operators.",
  },
  twilio_owner: {
    q: "Who should own the Twilio account?",
    a: "Normally the TAXI OPERATOR owns their own Twilio account. That way they own their phone numbers and pay call/SMS costs directly, and you're not in the billing path. You only need their Account SID, Auth Token and the numbers — pasted into the deployment env at deploy time (never stored here).",
  },
  twilio_sid: {
    q: "What is a Twilio Account SID and where do I find it?",
    a: "The Account SID is your Twilio account's public identifier (starts with 'AC'). Find it on the Twilio Console dashboard home page, under 'Account Info'. It's safe to store; the Auth Token next to it is the secret.",
  },
  twilio_auth_token: {
    q: "Where do I find my Twilio Auth Token?",
    a: "On the Twilio Console dashboard home, in 'Account Info', next to the Account SID. It's a secret — treat it like a password. This wizard never stores it; you paste it straight into the deployment's environment at deploy time.",
  },
  public_booking_number: {
    q: "Why do I need a public booking number?",
    a: "It's the phone number customers call to book a ride. Incoming calls are routed (via the generated Twilio voice webhook) to your dispatcher. Buy/port it in Twilio, then add it in the Twilio step. The generated Twilio checklist tells you exactly which webhook URLs to set.",
  },
  dispatcher_number: {
    q: "What is the dispatcher destination number?",
    a: "It's the real phone the public booking line forwards to (your control room / dispatcher's phone). Customers never see it — they only ever dial the public booking number.",
  },
  existing_number: {
    q: "What if the operator already has a booking phone number?",
    a: "They can port it into Twilio (keeps the same number for customers) or set up call-forwarding from it to the new Twilio booking number. Porting is cleaner long-term; forwarding is faster to launch.",
  },
  resend_what: {
    q: "What is Resend used for?",
    a: "Resend is the email provider Dispatch OS uses to send transactional email — password resets, booking confirmations, corporate statements, invites. You verify a sending domain (e.g. mail.theirdomain.co.uk) so email lands in inboxes rather than spam.",
  },
  verify_domain: {
    q: "How do I verify a sending domain in Resend?",
    a: "In Resend → Domains → Add domain, enter the sending domain (e.g. mail.theirdomain.co.uk). Resend shows the exact DNS records to add (SPF, DKIM, sometimes MX). Add them at the domain's DNS host, then click Verify and wait for all records to go green. The generated DNS + Resend checklists list these.",
  },
  dkim: {
    q: "What is DKIM?",
    a: "DKIM (DomainKeys Identified Mail) cryptographically signs your outgoing email so receivers can confirm it really came from your domain and wasn't tampered with. Resend gives you DKIM records (usually CNAMEs) to add to DNS. Without it, email is far more likely to be marked as spam.",
  },
  spf: {
    q: "What is SPF?",
    a: "SPF (Sender Policy Framework) is a DNS TXT record that lists which servers are allowed to send email for your domain. Adding Resend's SPF record tells receivers that Resend is authorised to send on your behalf.",
  },
  mx: {
    q: "What are MX records?",
    a: "MX (Mail eXchange) records tell the internet which servers receive email for a domain. Resend may ask for an MX record on your sending subdomain for bounce/feedback handling. Add exactly what the Resend dashboard shows.",
  },
  emails_failing: {
    q: "Why are emails failing / going to spam?",
    a: "Almost always the sending domain isn't fully verified. Check Resend → Domains: every SPF/DKIM (and any MX) record must be green. DNS changes can take a little while to propagate. Until verified, only email to the account owner's own address tends to work.",
  },
  stripe_why: {
    q: "Why do I need Stripe?",
    a: "Only if the operator wants to take CARD payments. Cash and on-account (corporate) rides work without Stripe. If they want cards, Stripe processes the payment and Dispatch OS records it against the ride.",
  },
  stripe_keys: {
    q: "What Stripe keys do I need?",
    a: "Three values: the Publishable key (pk_…, safe to expose), the Secret key (sk_…, keep private), and the Webhook signing secret (whsec_…) created when you add the Stripe webhook endpoint. The secret + webhook secret are pasted into the deployment env at deploy time — never stored here.",
  },
  stripe_test: {
    q: "What should I test in Stripe before going live?",
    a: "In test mode, run a payment with card 4242 4242 4242 4242 (any future expiry/CVC) and confirm it shows in Stripe and the ride is marked paid. Then run a refund. Switch to LIVE keys only once both work. The generated Stripe checklist + go-live test plan cover this.",
  },
  need_own_domain: {
    q: "Does the operator need their own domain?",
    a: "Yes, recommended — they run on their own dispatch subdomain (e.g. dispatch.abctaxis.co.uk) so the app and emails are clearly theirs. They buy the domain at any registrar; you point the dispatch subdomain at the hosting and use a mail subdomain for Resend.",
  },
  support_email: {
    q: "What is the support email for?",
    a: "It's the reply-to / contact address shown to customers and used on outgoing email (e.g. support@theirdomain.co.uk). Use a real, monitored inbox.",
  },
  first_admin: {
    q: "How is the first admin account set up?",
    a: "After the first deploy, the owner/admin account is seeded with the name/email captured here, and they set their password via an emailed link, then log in and change it. The generated First-Admin checklist lists the exact steps.",
  },
  first_login_test: {
    q: "What should I check on first login?",
    a: "Log in as admin, change the password, complete the in-app Setup wizard (company profile, licensing, base, fares, branding), confirm the landing page shows the operator's branding + licence number, then add the first driver and run one end-to-end test booking.",
  },
  operating_area: {
    q: "What is the operating area?",
    a: "The towns/districts the operator is licensed to cover. It's informational for setup and appears in operator details; it doesn't change dispatch logic.",
  },
  fares_help: {
    q: "How do fares work?",
    a: "You capture base fare, per-mile, per-minute, minimum fare, booking fee and VAT for the operator's tariff. These seed the deployment's fare settings. (Fare calculation logic itself is part of the protected core and isn't changed by this wizard.)",
  },
  db_hosting: {
    q: "Does the operator need to provide MongoDB or hosting?",
    a: "No — by default the Dispatch OS owner manages hosting and the database. The operator only provides Twilio, Resend, optionally Stripe, branding and company details. Only ask for MongoDB/hosting if your specific business model requires the operator to self-host.",
  },
  go_live_tests: {
    q: "What should I test before going live?",
    a: "Use the generated Go-Live Test Plan: admin/dispatcher/driver logins, driver online, create booking, driver accept, customer + 'driver arriving' SMS, voice masking, public booking line rings dispatcher, password-reset + confirmation emails, (if enabled) a test card payment + refund, and that the revenue dashboard updates and Ride Health shows no stuck rides.",
  },
};

export const UNSURE_REPLY =
  "I'm not sure from the current setup information. Please verify this in the provider dashboard (Twilio / Resend / Stripe / your DNS host).";
